import { CheckCircle2, Info, MoreHorizontal, X, XCircle, type LucideIcon } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { t } from "../i18n";

/* ------------------------------------------------------------------ dropdown menu */

export interface MenuItem {
  label: string;
  icon?: LucideIcon;
  onClick?: () => void;
  href?: string;
  danger?: boolean;
  disabled?: boolean;
  hint?: string;
}

export function Menu({ items, label = t("More actions"), buttonClass = "btn-secondary px-2.5" }: { items: (MenuItem | false | null | undefined)[]; label?: string; buttonClass?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const visible = items.filter(Boolean) as MenuItem[];
  if (!visible.length) return null;
  return (
    <div className="relative" ref={ref}>
      <button ref={trigger} type="button" className={buttonClass} aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-1.5 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
          {visible.map((it) => {
            const Icon = it.icon;
            const cls = `flex w-full items-start gap-2.5 px-3.5 py-2 text-left text-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-slate-800 ${it.danger ? "text-red-600 dark:text-red-400" : ""}`;
            const body = (
              <>
                {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0 opacity-70" />}
                <span className="min-w-0">
                  <span className="block">{it.label}</span>
                  {it.hint && <span className="block text-xs text-slate-500">{it.hint}</span>}
                </span>
              </>
            );
            return it.href ? (
              <a key={it.label} role="menuitem" href={it.href} className={cls} onClick={() => setOpen(false)}>{body}</a>
            ) : (
              <button key={it.label} role="menuitem" type="button" className={cls} disabled={it.disabled} onClick={() => { setOpen(false); trigger.current?.focus(); it.onClick?.(); }}>{body}</button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ modal dialog */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, title, description, children, footer, wide = false }: { open: boolean; onClose: () => void; title: string; description?: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const before = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
      if (e.key !== "Tab" || !box.current) return;
      const items = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // First field (or the dialog itself) gets focus, so keyboard users start inside the dialog.
    window.setTimeout(() => {
      const target = box.current?.querySelector<HTMLElement>("[data-autofocus]") ?? box.current?.querySelector<HTMLElement>("input, select, textarea") ?? box.current;
      target?.focus();
    }, 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      before?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-0 backdrop-blur-sm sm:items-center sm:p-4" onMouseDown={onClose}>
      <div ref={box} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title} className={`flex max-h-[92vh] w-full flex-col rounded-t-2xl bg-white shadow-xl dark:bg-slate-900 sm:rounded-2xl ${wide ? "sm:max-w-2xl" : "sm:max-w-lg"}`} onMouseDown={(e) => e.stopPropagation()}>
        <header className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
          </div>
          <button className="btn-ghost -mr-2 px-2" onClick={onClose} aria-label={t("Close")}><X className="h-4 w-4" /></button>
        </header>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
        {footer && <footer className="flex flex-wrap justify-end gap-2 border-t border-slate-100 px-5 py-3 dark:border-slate-800">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------ toasts */

interface Toast {
  id: number;
  text: string;
  tone: "success" | "error" | "info";
}

const ToastCtx = createContext<(text: string, tone?: Toast["tone"]) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const show = useCallback((text: string, tone: Toast["tone"] = "success") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4500);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2" aria-live="polite">
        {toasts.map((x) => (
          <div key={x.id} role={x.tone === "error" ? "alert" : "status"} className="pointer-events-auto flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-3.5 text-sm shadow-lg dark:border-slate-700 dark:bg-slate-900">
            {x.tone === "success" ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> : x.tone === "info" ? <Info className="h-4 w-4 shrink-0 text-sky-600" /> : <XCircle className="h-4 w-4 shrink-0 text-red-600" />}
            <span className="min-w-0 flex-1 break-words">{x.text}</span>
            <button className="-m-1 rounded p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" aria-label={t("Dismiss")} onClick={() => setToasts((all) => all.filter((item) => item.id !== x.id))}><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return useContext(ToastCtx);
}

/* ------------------------------------------------------------------ confirmation dialog */

interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}
type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };
const ConfirmCtx = createContext<(o: ConfirmOptions) => Promise<boolean>>(async () => false);

/** One consistent, keyboard-accessible confirmation for destructive actions (instead of window.confirm). */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const ask = useCallback((o: ConfirmOptions) => new Promise<boolean>((resolve) => setPending({ ...o, resolve })), []);
  const close = (ok: boolean) => { pending?.resolve(ok); setPending(null); };
  return (
    <ConfirmCtx.Provider value={ask}>
      {children}
      <Modal open={!!pending} onClose={() => close(false)} title={pending?.title ?? ""} description={pending?.description}
        footer={<>
          <button className="btn-secondary" onClick={() => close(false)}>{t("Cancel")}</button>
          <button data-autofocus className={pending?.danger ? "btn bg-red-600 text-white hover:bg-red-700" : "btn-primary"} onClick={() => close(true)}>{pending?.confirmLabel ?? t("Confirm")}</button>
        </>}>
        <p className="text-sm text-slate-600 dark:text-slate-300">{pending?.danger ? t("This cannot be undone.") : t("Please confirm to continue.")}</p>
      </Modal>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmCtx);
}
